import { Checkbox, Collapse, Flex, message, Popover, Select, Slider, Space, Spin, Steps, Table, Typography, Upload, UploadProps } from "antd";
import '@xyflow/react/dist/style.css';
import { InfoCircleOutlined, LoadingOutlined, SettingOutlined, UploadOutlined } from "@ant-design/icons";
import XLSX from 'xlsx'
import { useCallback, useEffect, useMemo, useState } from "react";
import { ReactGrid } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { detectPeaks } from "./peakDetection";
import { StyledButton } from "src/styled_components/StyledButton";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "src/store/configureStore";
import { StyledCard } from "src/styled_components/StyledCard";
import { dwtAnalysisRequest, dwtTransformClear, dwtTransformRequest, operationDatasetAddRequest, operationDetailsRequest } from "src/store/actions/operations";
import { StyledInput } from "src/styled_components/StyledInput";
import { Samples } from "src/mocks/db";
import { AsyncStates, defaultHeaders } from "src/constants";
import mergebounce from "mergebounce"
import jwtManager from "src/utils/jwtManager";
import { sendPostWholeUrl } from "src/utils/sendApi";
import { useVariableValue } from "@devcycle/devcycle-react-sdk";

const apiBaseUrl = process.env.REACT_APP_API_URL + 'v1/data'
const apiPushPath = "/_samples"

export const pushSamples = mergebounce(
  async (samples: any[]) => {
    try {
      const url = `${apiBaseUrl}${apiPushPath}`
      await sendPostWholeUrl(url)(samples, { ...defaultHeaders, token: jwtManager.getToken()! })
    } catch (error) {
      console.log(error)
    }
  },
  10,
  { promise: true, concatArrays: true }
)

export interface Dataset {
  name?: string
  type: string,
  id: string,
  placeholder?: true,
  dataset: {
    features: string[],
    experiments: string[],
    data: any[]
  },
  dwt_dataset?: {
    features: string[],
    experiments: string[],
    data: any[]
  },
  peak_dataset?: {
    features: string[],
    experiments: string[],
    data: any[]
  }
}

const getStep = (id: string) => {
  switch (id) {
    case "DATASET":
      return {
        id,
        title: "Upload Initial Data",
        description: (<>
          <Space><InfoCircleOutlined /><Typography.Text type="secondary">Preview Datasheet</Typography.Text></Space>
        </>)
      }
    case "NIR_DATASET":
      return {
        id,
        title: "NIR Spectra Data",
        description: (<>
          <Space><InfoCircleOutlined /><Typography.Text type="secondary">Upload raw NIR Spectra</Typography.Text></Space>
          <Space><InfoCircleOutlined /><Typography.Text type="secondary">Process NIR Data</Typography.Text></Space>
        </>)
      }
    case "PREVIEW":
      return {
        id,
        title: "Preview Data",
        description: (<>
          <Space><InfoCircleOutlined /><Typography.Text type="secondary">Preview Data</Typography.Text></Space>
        </>)
      }

    default:
      break;
  }
}

export const Datasets = () => {
  const dispatch = useDispatch()
  const { opid } = useParams<{ opid: string }>()
  const [isEditing, setIsEditing] = useState(false)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const detailsStatus = useSelector((state: StoreState) => state.operations.operationDetailsStatus)
  const details = useSelector((state: StoreState) => state.operations.operationDetailsData)
  const datasetsByType = useMemo(() => details.datasets?.filter((dataset: any) => dataset?.type === details.dataset_types[currentStepIndex]), [currentStepIndex, details.dataset_types, details.datasets])
  const placeholderDataset = useMemo<Dataset>(() => ({
    type: details?.dataset_types?.[currentStepIndex],
    id: crypto.randomUUID(),
    placeholder: true,
    dataset: {
      features: [],
      data: [],
      experiments: []
    }
  }), [currentStepIndex, details?.dataset_types])
  const [datasets, setDatasets] = useState<Dataset[]>(datasetsByType?.length ? datasetsByType : [placeholderDataset])
  console.log("🚀 ~ Datasets ~ datasets:", datasets)
  const [currentDatasetId, setCurrentDatasetId] = useState<string | undefined>(datasetsByType?.at(-1)?.id ?? placeholderDataset.id)
  const isPlaceholder = useMemo(() => datasets?.find(dataset => dataset.id === currentDatasetId)?.placeholder, [currentDatasetId, datasets])
  const datasetEntry = useMemo(() => datasets?.find(dataset => dataset.id === currentDatasetId), [currentDatasetId, datasets])
  const dataset = useMemo(() => datasetEntry?.dataset, [datasetEntry?.dataset])
  const dwtDataset = useMemo(() => datasetEntry?.dwt_dataset, [datasetEntry?.dwt_dataset])
  console.log("🚀 ~ Datasets ~ dwtDataset:", dwtDataset)
  useEffect(() => {
    setDatasets(datasetsByType?.length ? datasetsByType : [placeholderDataset])
    setCurrentDatasetId(datasetsByType?.at(-1)?.id ?? placeholderDataset.id)
  }, [datasetsByType, placeholderDataset, placeholderDataset.id])
  useEffect(() => {
    if (isPlaceholder) {
      setIsEditing(true)
    } else {
      setIsEditing(false)
    }
  }, [currentStepIndex, details.dataset_types, isPlaceholder])
  const [selectedExperiments, setSelectedExperiments] = useState(dataset?.experiments || [])
  console.log("🚀 ~ Datasets ~ selectedExperiments:", selectedExperiments)

  useEffect(() => {
    setSelectedExperiments(dataset?.experiments || [])
  }, [dataset?.experiments])

  useEffect(() => {
    if (!details?.id) {
      dispatch(operationDetailsRequest({ opid }))
    }
  }, [details?.id, dispatch, opid])

  const transform = useCallback(async (record: any) => {
    const fileSheets = await record.arrayBuffer()
    const workBook = XLSX.read(fileSheets, { dense: true, sheets: 0 })
    const config = { featureRowIndex: 0, experimentColumnIndex: 0 }
    const sheet = workBook.Sheets[workBook.SheetNames[0]]
    const range = XLSX.utils.decode_range(sheet["!ref"] ?? "")
    const featureRange = XLSX.utils.encode_range({
      ...range,
      s: { c: config.experimentColumnIndex + 1, r: config.featureRowIndex + 1 },
    })
    const features = sheet[config.featureRowIndex]
      .slice(config.experimentColumnIndex + 1)
      .map((column: XLSX.CellObject) => column.v)
      .map(String)
    const experiments = sheet
      .slice(config.featureRowIndex + 1)
      .map((row: XLSX.CellObject[]) => row[config.experimentColumnIndex].v)
      .map(String)
    const data = XLSX.utils.sheet_to_json(sheet, {
      blankrows: false,
      range: featureRange,
      header: features,
      raw: false
    })

    const { isValid, message: errorMessage } = { isValid: true, message: 'error' }

    if (isValid) {
      setDatasets(state => state.map(_dataset =>
        _dataset.id === currentDatasetId ?
          {
            ..._dataset,
            name: _dataset.name ?? record.name,
            dataset: {
              experiments,
              features,
              data
            }
          } :
          _dataset
      ))
      setIsEditing(true)
      dispatch(dwtAnalysisRequest({ experiments, features, data }))
    } else {
      message.error(errorMessage)
    }

    return false
  }, [currentDatasetId, dispatch])

  const props: UploadProps = useMemo(() => ({
    onRemove: (record: any) => {
    },
    accept: '.xlsx, .xls, .csv',
    beforeUpload: (record: any) => {
      transform(record)
      return false
    },
    multiple: false,
    maxCount: 1,
  }), [transform])

  const columns = useMemo(() => dataset ? [...(dataset?.features?.length ? [{ columnId: "Experiment", width: 150 }] : []), ...dataset?.features?.map(feature => ({ columnId: feature, width: 150 }))] : [], [dataset]);
  const rows = useMemo(() => dataset ? [
    ...(dataset?.features?.length ? [{ // header row
      rowId: "HEADER",
      cells: [{ type: "header", text: "Experiment" }, ...dataset?.features?.map(feature => ({ type: "header", text: Samples.findOne({ id: feature }, { fieldTracking: true })?.name ?? feature }))]
    }] : []),
    ...dataset?.data // body rows
      ?.map((row: string, idx: number) => ({
        rowId: dataset?.experiments?.[idx],
        cells: [
          {
            type: 'text',
            text: Samples.findOne({ id: dataset?.experiments?.[idx] }, { fieldTracking: true })?.name ?? dataset?.experiments?.[idx]
          },
          ...dataset?.features
            ?.map((feature: any) => ({
              type: 'text',
              text: row[feature]
            }))]
      })) || []] : [], [dataset]);

  const spectrums = useMemo(() => selectedExperiments
    ?.map((expId) => ({
      x: dataset?.features?.map(feature => Samples.findOne({ id: feature })?.name ?? feature).map(Number),
      y: dataset?.features?.map(feature => dataset?.data?.[dataset?.experiments?.indexOf(expId)]?.[feature]).map(Number)
    })
    ), [dataset?.data, dataset?.experiments, dataset?.features, selectedExperiments])

  const [peakSettings, setPeakSettings] = useState({
    coalesce: 2,
    lookaround: 8,
    sensitivity: 0,
    full: false
  })

  const experimentPeakIndexesList: any = useMemo(() => selectedExperiments
    .map((_, index) => detectPeaks(spectrums[index]?.y || [], null, peakSettings) || []), [peakSettings, selectedExperiments, spectrums])

  const experimentPeaksList: any = useMemo(() => experimentPeakIndexesList
    .map((experimentPeakIndexes: number[], experimentIndex: number) => {
      const peaks = experimentPeakIndexes.map(peakIndex => {
        const peak = dataset?.features?.[peakIndex]
        const wavelength = Samples.findOne({ id: peak })?.name ?? peak
        const intensity = peak && dataset?.data?.[experimentIndex][peak]
        return { wavelength: wavelength, intensity }
      })
      return peaks
    }), [dataset?.data, dataset?.features, experimentPeakIndexesList])

  const addDataset = () => {
    const newDataset = {
      id: crypto.randomUUID(),
      type: details?.dataset_types?.[currentStepIndex],
      dataset: {
        experiments: [],
        features: [],
        data: []
      }
    }
    setDatasets(state => [...state, newDataset])
    setCurrentDatasetId(newDataset.id)
    setIsEditing(true)
    dispatch(dwtTransformClear())
  }

  const steps = useMemo(() => (details?.dataset_types?.map((type: string) => getStep(type)) || [])?.concat(getStep("PREVIEW")), [details?.dataset_types])

  const discard = () => {
    setIsEditing(false)
    const currentDatasetIndex = datasets.findIndex(dataset => dataset.id === currentDatasetId)
    setCurrentDatasetId(datasets[Math.max(currentDatasetIndex - 1, 0)]?.id)
    setDatasets(state => state.filter(dataset => dataset.id !== currentDatasetId))
  }

  const series = useMemo(() => selectedExperiments
    ?.map(expId => ({
      name: Samples.findOne({ id: expId })?.name ?? expId,
      data: dataset?.features
        ?.map((feature: any) => ({
          x: Number(Samples.findOne({ id: feature })?.name ?? feature),
          y: Number(dataset?.data?.[dataset?.experiments?.indexOf(expId)]?.[feature])
        }))
    }))
    .concat([{
      name: 'Peaks',
      data: experimentPeakIndexesList[0]?.map((peak: number) => ({ x: spectrums[0].x?.[peak], y: spectrums[0].y?.[peak] }))
    }]), [dataset?.data, dataset?.experiments, dataset?.features, experimentPeakIndexesList, selectedExperiments, spectrums])
  console.log("🚀 ~ Datasets ~ series:", series)

  const peakParameters = useMemo(() => experimentPeaksList[0]
    ?.map((_: number, peakIndex: number) => {
      const experimentCurrentPeakWavelengthList = experimentPeaksList
        .map((experimentPeaks: any[]) => experimentPeaks[peakIndex]?.wavelength)
      const min = Math.min(...experimentCurrentPeakWavelengthList)
      const max = Math.max(...experimentCurrentPeakWavelengthList)
      return `${min}-${max}-${crypto.randomUUID().slice(-6)}`
    }) || [], [experimentPeaksList])

  const peakData = useMemo(() => selectedExperiments.map((experimentId, experimentIndex) => ({
    key: experimentId,
    experiment: Samples.findOne({ id: experimentId })?.name ?? experimentId,
    ...peakParameters?.reduce((acc: any, peakParameter: string, peakParameterIndex: number) => ({
      ...acc,
      [peakParameter]: experimentPeaksList[experimentIndex][peakParameterIndex]?.intensity
    }
    ), {})
  })
  ), [experimentPeaksList, peakParameters, selectedExperiments])

  const [selectedPeaks, setSelectedPeaks] = useState(peakParameters)
  useEffect(() => { setSelectedPeaks(peakParameters) }, [peakParameters])
  const dwtAnalysisData = useSelector((state: StoreState) => state.operations.dwtAnalysisData)
  const numDWTCoefficients = useVariableValue("num_dwt_coefficients", 0)
  const [noOfCoefficients, setNoOfCoefficients] = useState<Number>(numDWTCoefficients)
  const [waveletType, setWaveletType] = useState<String | undefined>(dwtAnalysisData?.best_parameters?.wavelet)

  const dwtTransform = () => {
    dispatch(dwtTransformRequest({
      params: {
        wavelet_type: waveletType,
        num_coefficients: noOfCoefficients
      },
      dataset
    }))
  }

  const dwtTransformData = useSelector((state: StoreState) => state.operations.dwtTransformData)
  const dwtTransformStatus = useSelector((state: StoreState) => state.operations.dwtTransformStatus)
  const operationDatasetAddStatus = useSelector((state: StoreState) => state.operations.operationDatasetAddStatus)

  useEffect(() => {
    if (dwtTransformStatus !== AsyncStates.INITIAL) {
      setDatasets(datasets => datasets.map(dataset => {
        if (dataset.id !== currentDatasetId) return dataset
        return {
          ...dataset,
          dwt_dataset: {
            experiments: dataset.dataset.experiments,
            features: Object.keys(dwtTransformData?.data?.[0] || {}),
            data: dwtTransformData.data || [],
            config: dwtTransformData?.transform_config
          }
        }
      })
      )
    }
  }, [currentDatasetId, dwtTransformData, dwtTransformStatus])

  const save = () => {
    const experimentMap = dataset?.experiments?.map(experiment => ({ id: crypto.randomUUID(), name: experiment })) ?? []
    const experiments = experimentMap.map(({ id }) => id)
    const featureMap = dataset?.features?.map(feature => ({ id: crypto.randomUUID(), name: feature })) ?? []
    const features = featureMap.map(({ id }) => id)
    const data = dataset?.data.map(row => featureMap?.reduce((acc, { name, id }) => ({ ...acc, [id]: row[name] }), {}))
    const samples = [
      ...experimentMap,
      ...featureMap
    ]
    setIsEditing(false)

    let newDatasetEntry: any = {
      ...datasetEntry,
      dataset: {
        features,
        experiments,
        data,
        samples
      },
      placeholder: undefined
    }

    if ("data" in dwtTransformData) {
      newDatasetEntry = {
        ...newDatasetEntry,
        dwt_dataset: {
          features: Object.keys(dwtTransformData?.data?.[0] || {}),
          data: dwtTransformData.data,
          experiments,
          config: {
            "num_coefficients": dwtTransformData?.transform_info?.num_coefficients,
            "wavelet_type": dwtTransformData?.transform_info?.wavelet_type
          }
        }
      }
    }
    if (peakData?.length) {
      newDatasetEntry = {
        ...newDatasetEntry,
        peak_dataset: {
          features: selectedPeaks,
          experiments,
          data: selectedExperiments.map((experimentId, experimentIndex) => ({
            ...selectedPeaks?.reduce((acc: any, peakParameter: string) => {
              const peakParameterIndex = peakParameters.findIndex((_peakParameter: string) => peakParameter === _peakParameter)
              return ({
                ...acc,
                [peakParameter]: experimentPeaksList[experimentIndex][peakParameterIndex]?.intensity
              }
              )
            }, {})
          })
          )
        }
      }
    }

    dispatch(operationDatasetAddRequest({
      opid,
      dataset_entry: newDatasetEntry
    }))
  }

  const peakColumns = useMemo(() => [
    {
      title: 'Experiment',
      dataIndex: 'experiment',
      fixed: "left"
    },
    ...peakParameters?.map((parameter: string) => ({
      dataIndex: parameter,
      title: <Checkbox defaultChecked={true} checked={!!selectedPeaks.find((peak: string) => peak === parameter)} onChange={e => { setSelectedPeaks((peaks: string[]) => e.target.checked ? peaks.concat(parameter) : peaks.filter((peak: string) => peak !== parameter)) }}>{parameter}</Checkbox>
    }))
  ], [peakParameters, selectedPeaks])

  const next = () => {
    setCurrentStepIndex(currentStepIndex + 1);
  };

  const previous = () => {
    setCurrentStepIndex(currentStepIndex - 1);
  };

  return (
    <Spin spinning={detailsStatus === AsyncStates.LOADING} indicator={<LoadingOutlined />}>
      <StyledCard style={{ marginBottom: 100 }}>
        {/* <div style={{ width: 800, height: 400 }}>
        <NodeEditor
          portTypes={config.portTypes}
          nodeTypes={config.nodeTypes}
          onChange={setNodes as any}
          nodes={nodes}
          initialScale={.5}
          defaultNodes={[
            {
              "x": -512,
              "y": -300,
              "type": "data"
            }
          ]}
        />
      </div> */}
        <div style={{ padding: 32, backdropFilter: "blur(10px)", position: "sticky", top: 0, background: "#ffffff40", zIndex: 2, marginInline: -24 }}>
          <Steps
            size="small"
            current={currentStepIndex}
            items={steps}
            onChange={step => { setCurrentStepIndex(step) }}
          />
        </div>
        {details.dataset_types?.[currentStepIndex] === "DATASET" && (
          <Space direction="vertical" style={{ width: "100%", paddingBottom: 60, padding: 8 }} size="large">
            {isEditing && <Upload.Dragger {...props}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Click to Upload or Drag and Drop </p>
              <p className="ant-upload-hint">CSV or Excel File only</p>
            </Upload.Dragger>}

            <div style={{ width: "100%", overflow: "auto" }}>
              <ReactGrid rows={rows} columns={columns} stickyLeftColumns={1} />
            </div>
          </Space>
        )}
        {details.dataset_types?.[currentStepIndex] === "NIR_DATASET" && (
          <Space direction="vertical" style={{ width: "100%" }}>
            {isEditing ? (<>
              <Typography.Text type="secondary">Type:</Typography.Text>
              <StyledInput size="small" style={{ width: 120 }} onChange={e => {
                setDatasets(datasets => datasets.map(dataset => dataset.id === currentDatasetId ? { ...dataset, name: e.target.value } : dataset))
              }} />
              <Upload.Dragger {...props}>
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">Click to Upload or Drag and Drop </p>
                <p className="ant-upload-hint">CSV or Excel File only</p>
              </Upload.Dragger>
            </>) : (<>
              {!!datasets.length && (<Space direction="vertical" style={{ paddingBlock: 8, width: "100%" }}>
                <Typography.Text type="secondary">Type:{" "}</Typography.Text>
                <Select
                  size="small"
                  defaultValue={currentDatasetId}
                  value={currentDatasetId}
                  style={{ width: 180 }}
                  onChange={(value: string) => {
                    setCurrentDatasetId(value)
                  }}
                  options={datasetsByType.map((dataset: Dataset) => ({ value: dataset.id, label: dataset.name }))}
                />
                <StyledButton size="small" type="link" onClick={addDataset} style={{ padding: 0 }}>+ Add New Dataset</StyledButton>
              </Space>)}
            </>)}
            <div style={{ width: "100%", overflow: "auto" }}>
              <ReactGrid rows={rows} columns={columns} stickyLeftColumns={1} />
            </div>
            <Collapse
              {...(!dataset?.features?.length && { collapsible: "disabled", activeKey: 0 })}
              size="large"
              items={[
                {
                  key: '1',
                  label: (<>
                    <Typography.Title level={5}>DWT Calculations</Typography.Title>
                    <Typography.Text type="secondary">Specify parameters for the DWT process</Typography.Text>
                  </>),
                  showArrow: false,
                  children: (
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {isEditing && <Flex justify="space-between" align="center">
                        <Space direction="vertical">
                          <Typography.Text type="secondary">No. of Coefficients:</Typography.Text>
                          <StyledInput readOnly={!!numDWTCoefficients} value={String(noOfCoefficients)} type="Number" size="small" onChange={e => { setNoOfCoefficients(Number(e.target.value)) }} />
                          <Typography.Text type="secondary">Coefficient Suggestion: {Math.min(...(dwtAnalysisData?.top_parameters?.map((parameter: any) => parameter?.coefficient_count ?? Number.POSITIVE_INFINITY) || [])) ?? "-"}</Typography.Text>
                        </Space>
                        <StyledButton type="primary" onClick={dwtTransform}>Perform DWT Calculations</StyledButton>
                      </Flex>}
                      {isEditing && <Table
                        style={{ height: 280, marginBottom: 16 }}
                        title={() => (<>
                          <Typography.Title level={5}>Wavelet Selection</Typography.Title>
                          <Typography.Text type="secondary">Download the reports to view all parameters and select desired wavelet type with to proceed.</Typography.Text>
                        </>)}
                        scroll={{ x: 'max-content', y: 200 }}
                        columns={[
                          {
                            title: "Wavelet Type",
                            dataIndex: "wavelet"
                          },
                          {
                            title: "Coefficients",
                            dataIndex: "selected_coefficients"
                          },
                          {
                            title: "Thresholds Used",
                            dataIndex: "threshold_ratio"
                          },
                          {
                            title: "RMSE",
                            dataIndex: "rmse"
                          }
                        ]}
                        dataSource={dwtAnalysisData?.top_parameters?.map((row: any, key: number) => ({ ...row, key }))}
                        pagination={false}
                        rowSelection={{
                          type: "radio",
                          onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
                            setWaveletType(selectedRows?.[0]?.wavelet)
                          },
                        }}
                      />}
                      <Table
                        title={() => `${dwtTransformData?.transform_info?.wavelet_type ?? ""} Wavelet Type`}
                        scroll={{ x: 'max-content' }}
                        columns={dwtDataset?.features?.map(feature => ({ title: feature, dataIndex: feature })) || []}
                        dataSource={dwtDataset?.data || []}
                        pagination={false}
                      />
                    </Space>
                  ),
                }]} />
            <Collapse
              {...(!dataset?.features?.length && { collapsible: "disabled", activeKey: 0 })}
              size="large"
              items={[
                {
                  key: '1',
                  label: (<>
                    <Typography.Title level={5}>Peak Identification</Typography.Title>
                    <Typography.Text type="secondary">Select the peak to process and generate models</Typography.Text>
                  </>),
                  showArrow: false,
                  children: (
                    <Space style={{ width: "100%" }} direction="vertical">
                      <Space align="start">
                        <Select
                          mode="multiple"
                          defaultValue={selectedExperiments}
                          value={selectedExperiments}
                          onChange={async value => {
                            setTimeout(() => { setSelectedExperiments(value) }, 100)
                          }}
                          options={dataset?.experiments
                            ?.map(expId => ({ value: expId, label: Samples.findOne({ id: expId })?.name }))
                          }
                          allowClear
                        />
                        <Popover placement="leftBottom" content={(
                          <Space direction="vertical" style={{ width: "100%" }}>
                            <Slider tooltip={{ formatter: value => `Coalesce: ${value}` }} min={0} max={15} step={1} defaultValue={peakSettings.coalesce} onAfterChange={(value) => { setPeakSettings(state => ({ ...state, coalesce: value })) }} />
                            <Slider tooltip={{ formatter: value => `Lookaround: ${value}` }} min={0} max={20} step={1} defaultValue={peakSettings.lookaround} onAfterChange={(value) => { setPeakSettings(state => ({ ...state, lookaround: value })) }} />
                            <Slider tooltip={{ formatter: value => `Sensitivity: ${value}` }} min={0} max={5} step={0.1} defaultValue={peakSettings.sensitivity} onAfterChange={(value) => { setPeakSettings(state => ({ ...state, sensitivity: value })) }} />
                          </Space>
                        )} title="Detection Setting" trigger="click">
                          <StyledButton icon={<SettingOutlined />} />
                        </Popover>
                      </Space>
                      <div style={{ width: "100%", overflow: "auto" }}>
                        <HighchartsReact highcharts={Highcharts} options={{
                          credits: {
                            enabled: false,
                          },

                          title: {
                            text: 'NIR Data',
                            align: 'left'
                          },

                          subtitle: {
                            text: 'By Experiment',
                            align: 'left'
                          },

                          yAxis: {
                            title: {
                              text: 'Experiments'
                            }
                          },

                          legend: {
                            layout: 'vertical',
                            align: 'right',
                            verticalAlign: 'middle'
                          },

                          plotOptions: {
                            series: {
                              label: {
                                connectorAllowed: false
                              }
                            }
                          },
                          series,

                          responsive: {
                            rules: [{
                              condition: {
                                maxWidth: 500
                              },
                              chartOptions: {
                                legend: {
                                  layout: 'horizontal',
                                  align: 'center',
                                  verticalAlign: 'bottom'
                                }
                              }
                            }]
                          }

                        }} />
                      </div>
                      <Table
                        scroll={{ x: 'max-content' }}
                        columns={peakColumns}
                        dataSource={peakData}
                        pagination={false}
                      />
                    </Space>
                  ),
                }]} />
          </Space>
        )
        }
        {
          details.dataset_types?.length === currentStepIndex && (
            <Space direction="vertical" style={{ width: "100%" }}>
              <Collapse
                activeKey={1}
                size="large"
                items={[
                  {
                    key: '1',
                    label: (<>
                      <Typography.Title level={5}>Uploaded Dataset</Typography.Title>
                      <Typography.Text type="secondary">Review the uploaded master datasheet</Typography.Text>
                    </>),
                    showArrow: false,
                    children: (<Space direction="vertical" style={{ width: "100%", overflow: "auto" }}>
                      {details.datasets
                        ?.filter((dataset: any) => dataset.type === "DATASET")
                        ?.map((dataset: any, key: number) => <div key={`DATASET_${key}`}><Preview dataset={dataset.dataset} /></div>)}
                    </Space>),
                  }]} />
              <Collapse
                activeKey={1}
                size="large"
                items={[
                  {
                    key: '1',
                    label: (<>
                      <Typography.Title level={5}>Processed NIR Data</Typography.Title>
                      <Typography.Text type="secondary">Processed NIR Data with Peak Identification and DWT Caleculations</Typography.Text>
                    </>),
                    showArrow: false,
                    children: (<Space direction="vertical" style={{ width: "100%", overflow: "auto" }}>
                      {details.datasets
                        ?.filter((dataset: any) => dataset.type === "NIR_DATASET")
                        ?.map((dataset: any, key: number) => (
                          <StyledCard key={`NIR_DATASET_${key}`} title={dataset?.name}>
                            <Preview dataset={dataset.dwt_dataset} />
                            <Preview dataset={dataset.peak_dataset} />
                          </StyledCard>
                        ))}
                    </Space>),
                  }]} />

              {
              }
            </Space>)
        }
        <Flex style={{ marginTop: 24 }} justify="space-between">
          <div>
            {currentStepIndex > 0 && (
              <StyledButton size="small" style={{ margin: '0 8px' }} onClick={isEditing ? discard : previous}>
                Back
              </StyledButton>
            )}
          </div>
          <Space>
            {details.dataset_types?.length !== currentStepIndex && isEditing && (
              <StyledButton size="small" onClick={save} loading={operationDatasetAddStatus === AsyncStates.LOADING}>
                Save
              </StyledButton>
            )}
            {currentStepIndex < steps.length - 1 && !isEditing && (
              <StyledButton size="small" type="primary" onClick={next} loading={operationDatasetAddStatus === AsyncStates.LOADING}>
                Next
              </StyledButton>
            )}
          </Space>
        </Flex>
      </StyledCard>
    </Spin>
  )
}

const Preview = ({ dataset }: any) => {
  const columns = useMemo(() => [...(dataset?.features?.length ? [{ columnId: "Experiment", width: 150 }] : []), ...(dataset?.features?.map((feature: string) => ({ columnId: feature, width: 150 })) || [])], [dataset?.features]);
  const rows = useMemo(() => [
    ...(dataset?.features?.length ? [{ // header row
      rowId: "HEADER",
      cells: [{ type: "header", text: "Experiment" }, ...dataset?.features?.map((feature: string) => ({ type: "header", text: Samples.findOne({ id: feature })?.name ?? feature ?? "-" }))]
    }] : []),
    ...dataset?.data // body rows
      ?.map((row: any, idx: number) => ({
        rowId: dataset?.experiments?.[idx],
        cells: [
          {
            type: 'text',
            text: Samples.findOne({ id: dataset?.experiments?.[idx] })?.name ?? dataset?.experiments?.[idx] ?? "-"
          },
          ...dataset?.features
            ?.map((feature: any) => ({
              type: 'text',
              text: String(row[feature]) ?? "-"
            }))]
      })) || []], [dataset?.data, dataset?.experiments, dataset?.features]);
  return (
    <div style={{ width: "100%", overflow: "auto" }}>
      <ReactGrid rows={rows} columns={columns} stickyLeftColumns={1} />
    </div>
  )
}
